import FileExtIcon from '@core/components/ApplyDialogV2/assets/FileExtIcon'
import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import React, { type FC } from 'react'
import './FileUploaderV2ListItem.scss'
import TrashIcon from '@core/components/ApplyDialogV2/assets/TrashIcon'

const baseClass = 'FileUploaderV2ListItem'

interface FileUploaderV2ListItemProps {
    className?: string
    file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    progress?: number
    status?: 'uploading' | 'success' | 'default' | 'error'
    fileError?: string
    onDelete?: (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => void | Promise<void>
    oldCv?: boolean
    isShowDelete?: boolean
    loading: boolean
}

const FileUploaderV2ListItem: FC<FileUploaderV2ListItemProps> = ({
    file,
    progress = 0,
    status = 'default',
    fileError,
    onDelete,
    oldCv = false,
    className = '',
    isShowDelete = true,
    loading = false,
}) => {
    if (!file) {
        return null
    }

    const lastModified = (() => {
        if ('lastModified' in file) {
            return file.lastModified || 0
        } else if ('uploaded' in file) {
            const uploaded = Date.parse(file.uploaded ?? '') // string → number
            return isNaN(uploaded) ? 0 : uploaded
        }
        return 0
    })()

    const { name, size } = file

    const fileSizeKB = size ? (size / 1024).toFixed(0) : '0'

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024 * 1024) {
            return `${Math.round(bytes / 1024)} KB`
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }
    const fileSizeDisplay = size ? formatFileSize(size) : '0 KB'

    const formattedDate = lastModified
        ? new Date(lastModified).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : ''

    const handleDelete = () => {
        if (onDelete) onDelete(file)
    }

    const renderContent = () => {
        if (loading) {
            return <ApplySkeleton isLoading={true} style={{ width: `100%`, height: `100%` }} />
        }
        if (fileError) {
            return (
                <>
                    {/*<div className={`${baseClass}__file-info ${baseClass}__file-info--error`}>*/}
                    <FileExtIcon
                        file={file}
                        className={`${baseClass}__icon ${baseClass}__icon--error`}
                        width={32}
                        height={32}
                    />
                    <div className={`${baseClass}__details`}>
                        <span className={`${baseClass}__file-name`}>{name}</span>
                        <span className={`${baseClass}__file-meta ${baseClass}__error-message`}>{fileError}</span>
                    </div>
                    {/*</div>*/}
                    {isShowDelete && (
                        <button
                            className={`${baseClass}__delete-button`}
                            aria-label="Delete file"
                            onClick={handleDelete}
                            type="button"
                        >
                            <TrashIcon />
                        </button>
                    )}
                </>
            )
        }

        switch (status) {
            case 'uploading':
                return (
                    <>
                        <div className={`${baseClass}__file-info ${baseClass}__file-info--${status}`}>
                            <FileExtIcon file={file} className={`${baseClass}__icon`} width={32} height={32} />
                            {/*<span className={`${baseClass}__file-name`}>Attaching '{name}'</span>*/}
                            <span className={`${baseClass}__file-name ${baseClass}__file-name--${status}`}>{name}</span>
                        </div>
                        <div className={`${baseClass}__progress-container ${baseClass}__progress-container--${status}`}>
                            <div
                                className={`${baseClass}__progress-bar ${baseClass}__progress-bar--${status}`}
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <span className={`${baseClass}__progress-text ${baseClass}__progress-text--${status}`}>
                            {progress}%
                        </span>
                    </>
                )
            case 'success':
                return (
                    <>
                        <div className={`${baseClass}__file-info`}>
                            <FileExtIcon file={file} className={`${baseClass}__icon`} width={32} height={32} />
                            <div className={`${baseClass}__details`}>
                                <span className={`${baseClass}__file-name`}>{name}</span>
                                <span className={`${baseClass}__file-meta`}>
                                    {formattedDate}, {fileSizeDisplay}
                                </span>
                            </div>
                        </div>
                        {isShowDelete && (
                            <button
                                className={`${baseClass}__delete-button`}
                                aria-label="Delete file"
                                onClick={handleDelete}
                                type={'button'}
                            >
                                <TrashIcon />
                            </button>
                        )}
                    </>
                )
            // case 'error':
            //     return (
            //         <>
            //             <div className={`${baseClass}__file-info ${baseClass}__file-info--error`}>
            //                 <ErrorIcon className={`${baseClass}__icon ${baseClass}__icon--error`} />
            //                 <div className={`${baseClass}__details`}>
            //                     <span className={`${baseClass}__file-name`}>{name}</span>
            //                     <span className={`${baseClass}__error-message`}>
            //                         Error uploading the file. Please try again.
            //                     </span>
            //                 </div>
            //             </div>
            //             <button className={`${baseClass}__delete-button`} aria-label="Delete file">
            //                 <TrashIcon />
            //             </button>
            //         </>
            //     )
            default:
                return (
                    <>
                        <FileExtIcon file={file} className={`${baseClass}__icon`} width={32} height={32} />
                        <div className={`${baseClass}__details`}>
                            <span className={`${baseClass}__file-name`}>{name}</span>
                            <span className={`${baseClass}__file-meta`}>
                                {formattedDate}, {fileSizeKB} KB
                            </span>
                        </div>
                        {isShowDelete && (
                            <button
                                className={`${baseClass}__delete-button`}
                                aria-label="Delete file"
                                onClick={handleDelete}
                                type={'button'}
                            >
                                <TrashIcon />
                            </button>
                        )}
                    </>
                )
        }
    }

    return (
        <div
            className={[baseClass, className, fileError ? `${baseClass}--error` : `${baseClass}--${status}`].join(' ')}
        >
            {renderContent()}
        </div>
    )
}

export default FileUploaderV2ListItem
